import { BreadNormalCss, BreadSmallCss } from '@/styles/typography';
import { Link } from 'gatsby';
import styled, { css } from 'styled-components';

const Header = styled.header<{
  $bgColor: string;
  $isScrolled: boolean;
  $position?: 'fixed' | 'absolute';
  $banner?: boolean;
  $desktopPosition?: 'fixed' | 'absolute';
}>`
  position: ${({ $position, $banner }) =>
    $position || $banner ? 'absolute' : 'fixed'};
  top: ${({ $banner }) => ($banner ? '50px' : 0)};
  width: 100%;
  z-index: 100;
  padding: 13px 0;
  transition: background-color 0.3s ease-in-out;
  background: ${({ theme, $isScrolled }) =>
    theme.colors[$isScrolled ? 'white' : 'transparent']};

  ${({ $isScrolled, $position }) =>
    $isScrolled &&
    $position !== 'absolute' &&
    `
    position: fixed !important;
    top: 0;
  `}

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    position: ${({ $desktopPosition, $position, $banner }) =>
      $desktopPosition || $position || $banner ? 'absolute' : 'fixed'};

    background: ${({ theme, $desktopPosition, $isScrolled }) =>
      $isScrolled
        ? theme.colors.white
        : $desktopPosition === 'fixed'
          ? theme.colors.white
          : 'transparent'};
  }
`;

const Logo = styled.img<{
  $isScrolled?: boolean;
  $textColor?: 'white' | 'black';
}>`
  width: 100px;
  height: auto;
  flex-shrink: 0;
  display: flex;
  align-self: center;

  ${({ $isScrolled, $textColor }) =>
    ($isScrolled || $textColor === 'black') && `filter: invert(1);`};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    width: 180px;
  }
`;

const Hamburger = styled.button<{
  $isScrolled?: boolean;
  $textColor?: 'white' | 'black';
}>`
  display: block;
  position: relative;
  width: 50px;
  height: 50px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  left: -7.5px;

  &:hover {
    span {
      background: ${({ theme }) => theme.colors.orangePeel};
    }
  }
  span {
    position: absolute;
    left: 7.5px;
    width: 35px;
    height: 1px;
    background: ${({ theme, $isScrolled, $textColor }) =>
      theme.colors[$isScrolled || $textColor === 'black' ? 'black' : 'white']};
  }

  span:nth-child(1) {
    top: 16px;
  }

  span:nth-child(2) {
    bottom: 16px;
  }
`;

const Nav = styled.nav<{ open?: boolean }>`
  background-color: ${({ theme }) => theme.colors.white};
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100%;
  max-width: 474px;
  display: flex;
  flex-direction: column;
  padding: 92px 16px 200px;
  gap: 50px;
  overflow-y: scroll;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;
  z-index: 999999;
  transition: all 0.3s ease-in-out;
  left: ${({ open }) => (open ? 0 : '-474px')};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    gap: 40px;
    padding: 76px 50px 76px 60px;
    left: ${({ open }) => (open ? 0 : '-474px')};
  }
`;

const NavCloseButton = styled.button`
  position: absolute;
  background-color: red;
  top: 18px;
  right: 18px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 999999999999;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    &:hover {
      * {
        fill: ${({ theme }) => theme.colors.orangePeel};
      }
    }
  }
`;

const SubNavCloseButton = styled.button<{ $mobile?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  z-index: 999999999999;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    svg {
      display: ${({ $mobile }) => ($mobile ? 'none' : 'block')};
    }

    &:hover {
      * {
        fill: ${({ theme }) => theme.colors.orangePeel};
      }
    }
  }
`;

const Label = styled.p`
  ${BreadNormalCss}
  color: black;
  font-weight: 500;
`;

const SubNav = styled(Nav)<{ open?: boolean }>`
  background-color: ${({ theme }) => theme.colors.lightGrey};
  padding: 92px 16px 200px;
  z-index: 9999999;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    z-index: 99999;
    padding: 76px 50px;
    left: ${({ open }) => (open ? '474px' : '-474px')};

    ${NavCloseButton} {
      display: none;
    }
  }
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin-top: 20px;
  width: 100%;
  gap: 20px;
`;

const SmallNavList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 13px;
  width: 100%;
  list-style: none;
  margin-bottom: 56px;
  padding-top: 50px;
  border-top: 1px solid ${({ theme }) => theme.colors.lineGrey};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    border: none;
    margin: 0;
    padding: 0;
  }
`;

const SubNavs = styled.ul<{ $active?: boolean }>`
  position: absolute;
  top: 92px;
  left: 16px;
  right: 16px;
  z-index: 99999999999;
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    top: 76px;
    left: 50px;
    right: 50px;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
`;

const Banner = styled.div`
  background: ${({ theme }) => theme.colors.deepBlack};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  height: 50px;
  z-index: 100;
  width: 100%;
  padding: 0 10px;
`;

const BannerLabelCss = css`
  border-radius: 4px;
  border: 1px solid rgba(255, 153, 0, 0.63);
  background: rgba(255, 153, 0, 0.14);
  color: ${({ theme }) => theme.colors.offWhite};
  padding: 3px 10px;
  text-align: center;
  ${BreadSmallCss}
  font-size: clamp(10px, 1.5vw, 14px);
  white-space: nowrap;
`;

const BannerLabel = styled.span`
  ${BannerLabelCss}
`;

const BannerLabelLink = styled(Link)`
  ${BannerLabelCss}
  text-decoration: none;
`;

const BannerLink = styled(Link)`
  text-decoration: none;
`;

const BreadSmall = styled.span`
  ${BreadSmallCss}
  color: ${({ theme }) => theme.colors.white};
  font-size: clamp(10px, 1.5vw, 14px);
`;

const BannerSpan = styled.span`
  ${BreadSmallCss}
  color: ${({ theme }) => theme.colors.white};
  font-size: clamp(10px, 1.5vw, 14px);
  text-decoration: underline;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.orangePeel};
  }
`;

const LangLink = styled(Link)<{ $active?: boolean }>`
  ${BreadNormalCss}
  text-decoration: none;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.black : theme.colors.lineGrey};
  ${({ $active }) =>
    $active &&
    `
    pointer-events: none;
  `}
`;

const S = {
  Header,
  Logo,
  Hamburger,
  Label,
  Nav,
  SubNav,
  NavCloseButton,
  SubNavCloseButton,
  NavList,
  SmallNavList,
  SubNavs,
  Overlay,
  Banner,
  BannerLabel,
  BannerLabelLink,
  BreadSmall,
  BannerLink,
  BannerSpan,
  LangLink,
};

export default S;
